import {
  getAdjacencyCache,
  topologyFaceCount,
  vertexNeighbors,
} from "../modeling/topology.ts";
import type { PolygonTopology } from "../modeling/types.ts";

export type OverlayKind = "vertex" | "face";

const MIN_VERTEX_BUDGET = 4096;
const MAX_VERTEX_BUDGET = 65536;
const MIN_FACE_BUDGET = 2048;
const MAX_FACE_BUDGET = 32768;
const INITIAL_REFINEMENT_BUDGET = 4096;

export function overlayBudgetForViewport(
  width: number,
  height: number,
  kind: OverlayKind,
): number {
  const area = Math.max(1, width * height);
  if (kind === "vertex") {
    return Math.max(
      MIN_VERTEX_BUDGET,
      Math.min(MAX_VERTEX_BUDGET, Math.floor(area / 18)),
    );
  }
  return Math.max(
    MIN_FACE_BUDGET,
    Math.min(MAX_FACE_BUDGET, Math.floor(area / 28)),
  );
}

export function initialRefinementBudget(targetBudget: number): number {
  return Math.min(targetBudget, INITIAL_REFINEMENT_BUDGET);
}

export function nextRefinementBudget(
  currentBudget: number,
  targetBudget: number,
): number {
  if (currentBudget >= targetBudget) return targetBudget;
  return Math.min(
    targetBudget,
    Math.max(currentBudget + INITIAL_REFINEMENT_BUDGET, currentBudget * 2),
  );
}

function addVertex(
  out: number[],
  seen: Set<number>,
  vi: number,
  hiddenVertices: ReadonlySet<number>,
  budget: number,
): boolean {
  if (out.length >= budget || hiddenVertices.has(vi) || seen.has(vi)) {
    return out.length < budget;
  }
  seen.add(vi);
  out.push(vi);
  return out.length < budget;
}

function addRequiredVertex(
  out: number[],
  seen: Set<number>,
  vi: number,
  hiddenVertices: ReadonlySet<number>,
): void {
  if (hiddenVertices.has(vi) || seen.has(vi)) return;
  seen.add(vi);
  out.push(vi);
}

export function buildBudgetedVertexOverlay(
  topology: PolygonTopology,
  vertexCount: number,
  selectedVertices: ReadonlySet<number>,
  hiddenVertices: ReadonlySet<number>,
  budget: number,
): Uint32Array {
  const visibleCount = Math.max(0, vertexCount - hiddenVertices.size);
  if (visibleCount <= budget) {
    const all = new Uint32Array(visibleCount);
    let w = 0;
    for (let vi = 0; vi < vertexCount; vi++) {
      if (!hiddenVertices.has(vi)) all[w++] = vi;
    }
    return all;
  }

  const out: number[] = [];
  const seen = new Set<number>();
  const cache = selectedVertices.size
    ? getAdjacencyCache(topology, vertexCount)
    : null;

  for (const vi of selectedVertices) {
    addRequiredVertex(out, seen, vi, hiddenVertices);
  }
  if (out.length >= budget) return Uint32Array.from(out);

  for (const vi of selectedVertices) {
    if (!cache) continue;
    for (const neighbor of vertexNeighbors(cache, topology, vi)) {
      if (!addVertex(out, seen, neighbor, hiddenVertices, budget)) {
        return Uint32Array.from(out);
      }
    }
  }

  const remaining = Math.max(1, budget - out.length);
  const stride = Math.max(1, Math.floor(vertexCount / remaining));
  for (let vi = 0; vi < vertexCount && out.length < budget; vi += stride) {
    addVertex(out, seen, vi, hiddenVertices, budget);
  }
  for (let vi = 0; vi < vertexCount && out.length < budget; vi++) {
    addVertex(out, seen, vi, hiddenVertices, budget);
  }

  return Uint32Array.from(out);
}

function addFace(
  out: number[],
  seen: Set<number>,
  fi: number,
  hiddenFaces: ReadonlySet<number>,
  budget: number,
): boolean {
  if (out.length >= budget || hiddenFaces.has(fi) || seen.has(fi)) {
    return out.length < budget;
  }
  seen.add(fi);
  out.push(fi);
  return out.length < budget;
}

function addRequiredFace(
  out: number[],
  seen: Set<number>,
  fi: number,
  hiddenFaces: ReadonlySet<number>,
): void {
  if (hiddenFaces.has(fi) || seen.has(fi)) return;
  seen.add(fi);
  out.push(fi);
}

export function buildBudgetedFaceOverlay(
  topology: PolygonTopology,
  selectedFaces: ReadonlySet<number>,
  hiddenFaces: ReadonlySet<number>,
  budget: number,
): Uint32Array {
  const faceCount = topologyFaceCount(topology);
  const visibleCount = Math.max(0, faceCount - hiddenFaces.size);
  if (visibleCount <= budget) {
    const all = new Uint32Array(visibleCount);
    let w = 0;
    for (let fi = 0; fi < faceCount; fi++) {
      if (!hiddenFaces.has(fi)) all[w++] = fi;
    }
    return all;
  }

  const out: number[] = [];
  const seen = new Set<number>();
  for (const fi of selectedFaces) {
    addRequiredFace(out, seen, fi, hiddenFaces);
  }
  if (out.length >= budget) return Uint32Array.from(out);

  const remaining = Math.max(1, budget - out.length);
  const stride = Math.max(1, Math.floor(faceCount / remaining));
  for (let fi = 0; fi < faceCount && out.length < budget; fi += stride) {
    addFace(out, seen, fi, hiddenFaces, budget);
  }
  for (let fi = 0; fi < faceCount && out.length < budget; fi++) {
    addFace(out, seen, fi, hiddenFaces, budget);
  }

  return Uint32Array.from(out);
}
